import { useEffect, useState } from 'react'
import { Alert, Button, Form, Modal, Segmented, Select, Space, Typography } from 'antd'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { EMPTY_TABLE_STATE } from '@/shared/ui/table/tableState'
import {
  createSubscriber,
  fetchSubscribers,
  type DeliveryMethod,
} from '@/features/subscriber/subscriberApi'
import { SubscriberFormFields } from '@/features/subscriber/SubscriberFormFields'
import { convertLeadToSubscriber, type Lead } from './crmApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

/**
 * "Yutildi" — lidni to'lovchi mijozga aylantirish.
 *
 * <h2>Nega bu modal umuman kerak bo'ldi</h2>
 * Backendda "yutildi" holati BOR, lekin u MAVJUD abonent id'sini talab
 * qiladi (`POST /leads/{id}/convert/subscriber`). Panelda esa
 * "malakali lid → YANGI abonent" yo'li umuman yo'q edi: sotuvchi
 * "Yutildi" tugmasini bosardi va u — xaritadagi bo'shliq tufayli —
 * `/lost` ni chaqirardi, ya'ni lid YO'QOTILGAN bo'lib qolardi.
 *
 * <p>Shu sababli ikki yo'l ochiq qoldirildi va ular ATAYLAB
 * ajratilgan:
 * <ul>
 *   <li><b>Mavjud abonent</b> — mijoz allaqachon bazada (masalan
 *       o'zi kelib ro'yxatdan o'tgan). Faqat bog'lanadi;</li>
 *   <li><b>Yangi abonent</b> — hisob shu yerda ochiladi va darhol
 *       lidga bog'lanadi.</li>
 * </ul>
 *
 * <h2>⚠️ Abonentni PANEL yaratadi, `xsib-crm` EMAS</h2>
 * Aks holda `xsib-crm` ga `xsib-subscriber` uchun yangi servislararo
 * klient qo'shish kerak bo'lardi — ya'ni bitta amal uchun ikki servis
 * orasida yangi bog'liqlik. Panelda esa ikkala API ham allaqachon
 * ochiq, huquqlar ham alohida tekshiriladi (`subscriber.create` va
 * `crm.lead.convert`).
 *
 * <h2>⚠️ IKKI QADAM — va oradagi uzilish YASHIRILMAYDI</h2>
 * "Yangi abonent" yo'li ikki tarmoq chaqiruvi: hisob yaratish, keyin
 * bog'lash. Ikkinchisi yiqilsa abonent BAZADA QOLADI. Buni "xato" deb
 * ko'rsatish eng yomon variant bo'lardi: operator qaytadan urinib,
 * IKKINCHI abonent yaratib qo'yardi (yoki "login band" xatosiga
 * urilib, nima bo'layotganini tushunmasdi).
 *
 * <p>Shuning uchun yaratilgan abonent id'si holatda SAQLANADI va
 * "Qayta urinish" FAQAT bog'lash qadamini takrorlaydi. Xabar ham aniq:
 * "abonent yaratildi, lekin lidga bog'lanmadi".
 */
interface LeadWonModalProps {
  /** `null` — modal yopiq */
  lead: Lead | null
  onCancel: () => void
  /** Muvaffaqiyatli yakun — chaqiruvchi ro'yxatni yangilaydi */
  onDone: () => void
}

type WonMode = 'existing' | 'new'

interface NewSubscriberValues {
  login: string
  password?: string
  displayName?: string
  kind: DeliveryMethod
  /**
   * ⚠️ `SubscriberFormFields` bu maydonni CHIZADI, ya'ni uni bu yerda
   * o'qimaslik jimgina yo'qotish bo'lardi: sotuvchi lidni yutgach
   * agentni tanlaydi, panel abonentni yaratadi va `agent_id` bo'sh
   * qoladi. Lid orqali kelgan sotuv ham SOTUV — daromad o'sha agentga
   * yozilishi kerak.
   */
  agentId?: string | null
  simultaneousUse?: number | null
  staticIp?: string
}

export function LeadWonModal({ lead, onCancel, onDone }: LeadWonModalProps) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<WonMode>('existing')
  const [pickedSubscriberId, setPickedSubscriberId] = useState<string | undefined>()
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)

  /**
   * Yaratilgan, lekin hali bog'lanmagan abonent.
   *
   * ⚠️ Aynan shu holat "qayta urinish" ni xavfsiz qiladi: qiymat
   * turganida yaratish qadami QAYTA BAJARILMAYDI.
   */
  const [orphanSubscriber, setOrphanSubscriber] = useState<{ id: string; login: string } | null>(
    null,
  )

  const [form] = Form.useForm<NewSubscriberValues>()

  // Modal har ochilganda holat toza bo'lsin — aks holda oldingi lidning
  // tanlovi yangisiga o'tib ketardi va bu jimgina noto'g'ri bog'lanish.
  useEffect(() => {
    if (lead) {
      setMode('existing')
      setPickedSubscriberId(undefined)
      setSearch('')
      setError(null)
      setOrphanSubscriber(null)
      form.resetFields()
    }
  }, [lead, form])

  /*
    Abonent qidiruvi.

    ⚠️ Kamida 2 belgi: bo'sh so'rov butun ro'yxatni tortardi va
    tanlash oynasi birinchi 25 ta tasodifiy abonent bilan to'lardi —
    bu tanlashga yordam bermaydi, faqat chalg'itadi.
  */
  const candidates = useQuery({
    queryKey: ['crm', 'lead-won', 'subscriber-search', search],
    queryFn: ({ signal }) =>
      fetchSubscribers({ ...EMPTY_TABLE_STATE, search, pageSize: 20 }, signal),
    enabled: lead !== null && mode === 'existing' && search.trim().length >= 2,
  })

  const linkOnly = useMutation({
    mutationFn: ({ subscriberId }: { subscriberId: string }) =>
      convertLeadToSubscriber(lead!.id, subscriberId),
    onSuccess: () => {
      setOrphanSubscriber(null)
      onDone()
    },
    onError: (err) => setError(translateError(err, t)),
  })

  const createAndLink = useMutation({
    mutationFn: async (values: NewSubscriberValues) => {
      // 1-qadam — hisob. Yaratilgani zahoti holatga yoziladi, chunki
      // 2-qadam yiqilsa bu id "qayta urinish" uchun YAGONA yo'l.
      const created = await createSubscriber({
        login: values.login.trim(),
        password: values.password,
        displayName: values.displayName?.trim() || null,
        kind: values.kind,
        agentId: values.agentId ?? null,
        simultaneousUse: values.simultaneousUse ?? null,
        staticIp: values.staticIp?.trim() || null,
      })
      setOrphanSubscriber({ id: created.id, login: created.login })

      // 2-qadam — bog'lash.
      await convertLeadToSubscriber(lead!.id, created.id)
      return created
    },
    onSuccess: () => {
      setOrphanSubscriber(null)
      onDone()
    },
    onError: (err) => setError(translateError(err, t)),
  })

  const pending = linkOnly.isPending || createAndLink.isPending

  const submit = () => {
    setError(null)

    // Uzilgan holat: abonent bor, bog'lanish yo'q — faqat 2-qadam.
    if (orphanSubscriber) {
      linkOnly.mutate({ subscriberId: orphanSubscriber.id })
      return
    }

    if (mode === 'existing') {
      if (!pickedSubscriberId) {
        setError(t('crm.leadWon.pickRequired'))
        return
      }
      linkOnly.mutate({ subscriberId: pickedSubscriberId })
      return
    }

    submitOnValid(form, (values) => createAndLink.mutate(values))
  }

  return (
    <Modal
      open={lead !== null}
      title={t('crm.leadWon.title')}
      onCancel={onCancel}
      onOk={submit}
      confirmLoading={pending}
      okText={orphanSubscriber ? t('crm.leadWon.retryLink') : t('crm.leadWon.confirm')}
      cancelText={t('common.cancel')}
      width={560}
      destroyOnHidden
    >
      {lead ? (
        <Typography.Paragraph type="secondary">
          {t('crm.leadWon.subtitle', { name: lead.fullName })}
        </Typography.Paragraph>
      ) : null}

      {/*
        ⚠️ YARIM BAJARILGAN HOLAT — eng muhim xabar shu.
        "Xato" deb ko'rsatilsa operator boshidan boshlab, ikkinchi
        abonent yaratib qo'yardi.
      */}
      {orphanSubscriber ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('crm.leadWon.partialTitle')}
          description={t('crm.leadWon.partialHint', { login: orphanSubscriber.login })}
        />
      ) : null}

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          message={error}
          onClose={() => setError(null)}
        />
      ) : null}

      {orphanSubscriber ? null : (
        <>
          <Segmented<WonMode>
            block
            value={mode}
            onChange={(value) => {
              setMode(value)
              setError(null)
            }}
            options={[
              { value: 'existing', label: t('crm.leadWon.modeExisting') },
              { value: 'new', label: t('crm.leadWon.modeNew') },
            ]}
            style={{ marginBottom: 16 }}
          />

          {mode === 'existing' ? (
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
              <Typography.Text>{t('crm.leadWon.pickLabel')}</Typography.Text>
              <Select
                showSearch
                allowClear
                style={{ width: '100%' }}
                value={pickedSubscriberId}
                onChange={setPickedSubscriberId}
                onSearch={setSearch}
                // ⚠️ Filtrlash SERVERDA — mahalliy `filterOption` faqat
                // yuklab olingan 20 tani suzardi va operator "topilmadi"
                // degan noto'g'ri xulosaga kelardi.
                filterOption={false}
                loading={candidates.isFetching}
                notFoundContent={
                  search.trim().length < 2
                    ? t('crm.leadWon.searchHint')
                    : candidates.isFetching
                      ? t('common.loading')
                      : t('crm.leadWon.searchEmpty')
                }
                placeholder={t('crm.leadWon.searchPlaceholder')}
                options={(candidates.data?.items ?? []).map((row) => ({
                  value: row.subscriber.id,
                  label: row.subscriber.displayName
                    ? `${row.subscriber.login} — ${row.subscriber.displayName}`
                    : row.subscriber.login,
                }))}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('crm.leadWon.pickHint')}
              </Typography.Text>
            </Space>
          ) : (
            <Form<NewSubscriberValues>
              form={form}
              layout="vertical"
              preserve={false}
              initialValues={{
                // ⚠️ Faqat ISM oldindan to'ldiriladi. Telefon LIDDA
                // NIQOBLANGAN (`crm.lead.reveal` alohida huquq), ya'ni
                // uni bu yerga ko'chirish niqobning o'zini bekor
                // qilardi. Login esa operator qaroriga qoldiriladi:
                // u RADIUS kaliti va keyin o'zgartirib bo'lmaydi.
                displayName: lead?.fullName,
                kind: 'PPPOE' as DeliveryMethod,
              }}
            >
              <SubscriberFormFields mode="create" />
            </Form>
          )}
        </>
      )}

      {orphanSubscriber ? (
        <Space>
          <Button onClick={onCancel}>{t('crm.leadWon.leaveUnlinked')}</Button>
        </Space>
      ) : null}
    </Modal>
  )
}
